import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { getUserPermissions, userHasPermission } from '@/utils/permissions';

/**
 * React hook for managing user permissions with caching
 *
 * @returns {Object} Object containing:
 *   - permissions: Array of permission names
 *   - hasPermission: Function to check if user has a specific permission
 *   - isLoading: Boolean indicating if permissions are being fetched
 *   - error: Any error that occurred during fetch
 */
export function usePermissions() {
  const { user } = useAuth();

  const { data: permissions = [], isLoading, error } = useQuery({
    queryKey: ['permissions', user?.id],
    queryFn: () => user?.id ? getUserPermissions(user.id) : Promise.resolve([]),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  /**
   * Check if the current user has a specific permission
   * @param permissionName - The permission name to check
   * @returns True if user has the permission or system.override
   */
  const hasPermission = (permissionName: string): boolean => {
    return userHasPermission(permissions, permissionName);
  };

  /**
   * Check if the current user has any of the specified permissions
   * @param permissionNames - Array of permission names to check
   * @returns True if user has at least one of the permissions
   */
  const hasAnyPermission = (permissionNames: string[]): boolean => {
    return permissionNames.some(permission => hasPermission(permission));
  };

  return {
    permissions,
    hasPermission,
    hasAnyPermission,
    isLoading,
    error,
  };
}
